import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Timeline as GanttIcon,
  School as ProgramIcon,
  Groups as CohortIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { Gantt } from 'wx-react-gantt';
import 'wx-react-gantt/dist/gantt.css';
import './GanttShowcase.css';

const ganttColors = {
  primary: '#0078D4',
  secondary: '#40E0D0',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  orange: '#FF8C00',
  pink: '#E91E63',
  teal: '#009688',
  indigo: '#3F51B5',
};

const ChartCard = ({ title, subtitle, children, icon: Icon, gradientColors }) => {
  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        background: gradientColors 
          ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.05)} 0%, ${alpha(gradientColors[1], 0.05)} 100%)`
          : '#FFFFFF',
        borderRadius: 3,
        border: `1px solid ${alpha(ganttColors.primary, 0.12)}`,
        boxShadow: '0 8px 32px rgba(0, 120, 212, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradientColors
            ? `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`
            : `linear-gradient(90deg, ${ganttColors.primary}, ${ganttColors.secondary})`,
        },
        '&:hover': {
          boxShadow: '0 16px 48px rgba(0, 120, 212, 0.12)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: gradientColors
                ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.15)}, ${alpha(gradientColors[1], 0.15)})`
                : `linear-gradient(135deg, ${alpha(ganttColors.primary, 0.15)}, ${alpha(ganttColors.secondary, 0.15)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 24, color: gradientColors?.[0] || ganttColors.primary }} />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const GanttShowcase = () => {
  // Guayerd Programs Data - Formato correcto según documentación oficial
  const guayerdPrograms = {
    tasks: [
      {
        id: 1,
        text: 'Full Stack Development Bootcamp',
        start: new Date(2024, 0, 15),
        end: new Date(2024, 3, 30),
        duration: 105,
        progress: 85,
        type: 'summary',
        open: true,
      },
      {
        id: 2,
        text: 'Cohort 2024-Q1 (Online) - 180 students',
        start: new Date(2024, 0, 15),
        end: new Date(2024, 3, 30),
        duration: 105,
        progress: 100,
        parent: 1,
        type: 'task',
      },
      {
        id: 3,
        text: 'Cohort 2024-Q2 (Hybrid) - 220 students',
        start: new Date(2024, 3, 1),
        end: new Date(2024, 6, 15),
        duration: 105,
        progress: 85,
        parent: 1,
        type: 'task',
      },
      {
        id: 4,
        text: 'Cohort 2024-Q3 (On-site) - 120 students',
        start: new Date(2024, 6, 1),
        end: new Date(2024, 9, 15),
        duration: 105,
        progress: 60,
        parent: 1,
        type: 'task',
      },

      {
        id: 5,
        text: 'Data Science & AI Track',
        start: new Date(2024, 1, 1),
        end: new Date(2024, 5, 30),
        duration: 150,
        progress: 75,
        type: 'summary',
        open: true,
      },
      {
        id: 6,
        text: 'DS Cohort Alpha - 150 students',
        start: new Date(2024, 1, 1),
        end: new Date(2024, 5, 30),
        duration: 150,
        progress: 90,
        parent: 5,
        type: 'task',
      },
      {
        id: 7,
        text: 'DS Cohort Beta - 130 students',
        start: new Date(2024, 4, 1),
        end: new Date(2024, 8, 15),
        duration: 135,
        progress: 65,
        parent: 5,
        type: 'task',
      },
      {
        id: 8,
        text: 'DS Cohort Gamma - 100 students',
        start: new Date(2024, 7, 1),
        end: new Date(2024, 11, 20),
        duration: 140,
        progress: 30,
        parent: 5,
        type: 'task',
      },

      {
        id: 9,
        text: 'UX/UI Design Specialization',
        start: new Date(2024, 2, 1),
        end: new Date(2024, 6, 30),
        duration: 150,
        progress: 80,
        type: 'summary',
        open: true,
      },
      {
        id: 10,
        text: 'UX Cohort Spring - 95 students',
        start: new Date(2024, 2, 1),
        end: new Date(2024, 5, 15),
        duration: 105,
        progress: 100,
        parent: 9,
        type: 'task',
      },
      {
        id: 11,
        text: 'UX Cohort Summer - 110 students',
        start: new Date(2024, 5, 1),
        end: new Date(2024, 8, 15),
        duration: 105,
        progress: 70,
        parent: 9,
        type: 'task',
      },
      {
        id: 12,
        text: 'UI Cohort Advanced - 75 students',
        start: new Date(2024, 4, 15),
        end: new Date(2024, 6, 30),
        duration: 75,
        progress: 75,
        parent: 9,
        type: 'task',
      },

      {
        id: 13,
        text: 'Backend Development with Python',
        start: new Date(2024, 1, 15),
        end: new Date(2024, 4, 30),
        duration: 105,
        progress: 90,
        type: 'summary',
        open: true,
      },
      {
        id: 14,
        text: 'Backend Cohort Winter - 145 students',
        start: new Date(2024, 1, 15),
        end: new Date(2024, 4, 30),
        duration: 105,
        progress: 100,
        parent: 13,
        type: 'task',
      },
      {
        id: 15,
        text: 'Backend Cohort Spring - 165 students',
        start: new Date(2024, 3, 1),
        end: new Date(2024, 6, 15),
        duration: 105,
        progress: 95,
        parent: 13,
        type: 'task',
      },
      {
        id: 16,
        text: 'Backend Cohort Summer - 110 students',
        start: new Date(2024, 6, 1),
        end: new Date(2024, 9, 15),
        duration: 105,
        progress: 40,
        parent: 13,
        type: 'task',
      },

      {
        id: 17,
        text: 'Cloud Architecture & DevOps',
        start: new Date(2024, 3, 1),
        end: new Date(2024, 7, 30),
        duration: 150,
        progress: 60,
        type: 'summary',
        open: true,
      },
      {
        id: 18,
        text: 'DevOps Cohort Q2 - 82 students',
        start: new Date(2024, 3, 1),
        end: new Date(2024, 6, 15),
        duration: 105,
        progress: 85,
        parent: 17,
        type: 'task',
      },
      {
        id: 19,
        text: 'Cloud Cohort Q3 - 98 students',
        start: new Date(2024, 6, 1),
        end: new Date(2024, 9, 15),
        duration: 105,
        progress: 45,
        parent: 17,
        type: 'task',
      },

      {
        id: 20,
        text: 'Mobile Development (React Native)',
        start: new Date(2024, 4, 1),
        end: new Date(2024, 8, 30),
        duration: 150,
        progress: 55,
        type: 'summary',
        open: true,
      },
      {
        id: 21,
        text: 'Mobile Cohort May - 92 students',
        start: new Date(2024, 4, 1),
        end: new Date(2024, 7, 15),
        duration: 105,
        progress: 80,
        parent: 20,
        type: 'task',
      },
      {
        id: 22,
        text: 'Mobile Cohort Aug - 148 students',
        start: new Date(2024, 7, 1),
        end: new Date(2024, 10, 15),
        duration: 105,
        progress: 25,
        parent: 20,
        type: 'task',
      },
    ],
    links: [],
  };

  // Corporate Training Programs
  const corporatePrograms = {
    tasks: [
      {
        id: 1,
        text: 'Enterprise Digital Transformation',
        start: new Date(2024, 0, 8),
        end: new Date(2024, 5, 30),
        duration: 173,
        progress: 70,
        type: 'summary',
        open: true,
      },
      {
        id: 2,
        text: 'Phase 1: Assessment - Tech Corp SA',
        start: new Date(2024, 0, 8),
        end: new Date(2024, 1, 15),
        duration: 38,
        progress: 100,
        parent: 1,
        type: 'task',
      },
      {
        id: 3,
        text: 'Phase 2: Training - Tech Corp SA',
        start: new Date(2024, 1, 16),
        end: new Date(2024, 4, 30),
        duration: 105,
        progress: 80,
        parent: 1,
        type: 'task',
      },
      {
        id: 4,
        text: 'Phase 3: Implementation - Tech Corp SA',
        start: new Date(2024, 4, 1),
        end: new Date(2024, 5, 30),
        duration: 60,
        progress: 40,
        parent: 1,
        type: 'task',
      },

      {
        id: 5,
        text: 'Agile Methodologies Workshop',
        start: new Date(2024, 2, 1),
        end: new Date(2024, 3, 30),
        duration: 60,
        progress: 95,
        type: 'summary',
        open: true,
      },
      {
        id: 6,
        text: 'Scrum Master Certification - Innovation Labs',
        start: new Date(2024, 2, 1),
        end: new Date(2024, 3, 15),
        duration: 45,
        progress: 100,
        parent: 5,
        type: 'task',
      },
      {
        id: 7,
        text: 'Product Owner Training - Innovation Labs',
        start: new Date(2024, 3, 1),
        end: new Date(2024, 3, 30),
        duration: 30,
        progress: 100,
        parent: 5,
        type: 'task',
      },

      {
        id: 8,
        text: 'Leadership Development Program',
        start: new Date(2024, 1, 15),
        end: new Date(2024, 7, 30),
        duration: 195,
        progress: 65,
        type: 'summary',
        open: true,
      },
      {
        id: 9,
        text: 'Module 1: Strategic Thinking - Global Ventures',
        start: new Date(2024, 1, 15),
        end: new Date(2024, 3, 30),
        duration: 75,
        progress: 100,
        parent: 8,
        type: 'task',
      },
      {
        id: 10,
        text: 'Module 2: Team Management - Global Ventures',
        start: new Date(2024, 4, 1),
        end: new Date(2024, 5, 30),
        duration: 60,
        progress: 75,
        parent: 8,
        type: 'task',
      },
      {
        id: 11,
        text: 'Module 3: Change Leadership - Global Ventures',
        start: new Date(2024, 6, 1),
        end: new Date(2024, 7, 30),
        duration: 60,
        progress: 20,
        parent: 8,
        type: 'task',
      },
    ],
    links: [],
  };

  const [selectedView, setSelectedView] = useState('guayerd');

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setSelectedView(newView);
    }
  };

  const currentData = selectedView === 'guayerd' ? guayerdPrograms : corporatePrograms;

  const columns = [
    { id: 'text', header: 'Task name', width: 250 },
    { id: 'start', header: 'Start date', align: 'center', width: 110 },
    { id: 'duration', header: 'Duration', align: 'center', width: 80 },
  ];

  const scales = [
    { unit: 'month', step: 1, format: 'MMMM yyy' },
    { unit: 'day', step: 1, format: 'd' },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: { xs: 2, md: 4 }
    }}>
      {/* Hero Header */}
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            background: `linear-gradient(135deg, ${ganttColors.primary} 0%, ${ganttColors.purple} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Program & Cohort Timeline
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', fontWeight: 400, mb: 3 }}>
          Interactive Gantt charts showing Guayerd programs, cohorts, and corporate training schedules
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="Drag & Drop" color="primary" />
          <Chip label="Progress Tracking" sx={{ bgcolor: ganttColors.secondary, color: 'white' }} />
          <Chip label="Edit Form" sx={{ bgcolor: ganttColors.success, color: 'white' }} />
          <Chip label="Tree Structure" sx={{ bgcolor: ganttColors.warning, color: 'white' }} />
          <Chip label="Zoom & Scroll" sx={{ bgcolor: ganttColors.purple, color: 'white' }} />
        </Box>
      </Box>

      {/* View Selector */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={selectedView}
          exclusive
          onChange={handleViewChange}
          aria-label="program view"
          sx={{
            bgcolor: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 2,
            '& .MuiToggleButton-root': {
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                bgcolor: ganttColors.primary,
                color: 'white',
                '&:hover': {
                  bgcolor: alpha(ganttColors.primary, 0.9),
                },
              },
            },
          }}
        >
          <ToggleButton value="guayerd">
            <ProgramIcon sx={{ mr: 1 }} />
            Guayerd Programs & Cohorts
          </ToggleButton>
          <ToggleButton value="corporate">
            <CohortIcon sx={{ mr: 1 }} />
            Corporate Training
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Main Gantt Chart */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 0,
              borderRadius: 3,
              border: `1px solid ${alpha(ganttColors.primary, 0.12)}`,
              boxShadow: '0 8px 32px rgba(0, 120, 212, 0.08)',
              overflow: 'hidden',
              background: '#ffffff',
            }}
          >
            {/* Header */}
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #e8e8e8',
              background: `linear-gradient(135deg, ${alpha('#667eea', 0.05)} 0%, ${alpha('#764ba2', 0.05)} 100%)`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedView === 'guayerd' ? <ProgramIcon sx={{ fontSize: 24, color: '#667eea' }} /> : <CohortIcon sx={{ fontSize: 24, color: '#667eea' }} />}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    {selectedView === 'guayerd' ? 'Guayerd Academic Programs Timeline' : 'Corporate Training Programs'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Drag tasks to reschedule • Double-click to edit • Scroll to zoom
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Gantt - directamente sin Box wrapper */}
            <div className="wx-willow-theme" style={{ height: '650px' }}>
              <Gantt
                tasks={currentData.tasks}
                links={currentData.links}
                columns={columns}
                scales={scales}
                start={new Date(2024, 0, 1)}
                end={new Date(2024, 11, 31)}
                cellWidth={120}
                cellHeight={44}
                readonly={false}
              />
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Statistics Cards */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Program Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(ganttColors.primary, 0.1)}, ${alpha(ganttColors.primary, 0.05)})`,
            borderRadius: 3,
            border: `1px solid ${alpha(ganttColors.primary, 0.2)}`,
          }}>
            <ProgramIcon sx={{ fontSize: 48, color: ganttColors.primary, mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: ganttColors.primary, mb: 1 }}>
              6
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Active Programs
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(ganttColors.success, 0.1)}, ${alpha(ganttColors.success, 0.05)})`,
            borderRadius: 3,
            border: `1px solid ${alpha(ganttColors.success, 0.2)}`,
          }}>
            <CohortIcon sx={{ fontSize: 48, color: ganttColors.success, mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: ganttColors.success, mb: 1 }}>
              16
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Running Cohorts
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(ganttColors.warning, 0.1)}, ${alpha(ganttColors.warning, 0.05)})`,
            borderRadius: 3,
            border: `1px solid ${alpha(ganttColors.warning, 0.2)}`,
          }}>
            <GanttIcon sx={{ fontSize: 48, color: ganttColors.warning, mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: ganttColors.warning, mb: 1 }}>
              2,020
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Total Participants
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(ganttColors.purple, 0.1)}, ${alpha(ganttColors.purple, 0.05)})`,
            borderRadius: 3,
            border: `1px solid ${alpha(ganttColors.purple, 0.2)}`,
          }}>
            <CalendarIcon sx={{ fontSize: 48, color: ganttColors.purple, mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: ganttColors.purple, mb: 1 }}>
              72%
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Avg Progress
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Features Highlight */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Interactive Features
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: ganttColors.primary }}>
              🎯 Drag & Drop
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
              Click and drag taskbars to reschedule programs. Resize bars by dragging edges to adjust duration. Changes are immediate and visual.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: ganttColors.success }}>
              ✏️ Edit Form
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
              Double-click any task or row to open the edit form. Modify all task properties including dates, duration, and progress percentage.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: ganttColors.purple }}>
              🌳 Tree View
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
              Expand and collapse program branches to focus on specific cohorts. Navigate complex hierarchies with ease using the tree structure.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer info */}
      <Box sx={{ mt: 6, p: 4, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.9)', borderRadius: 3, border: `1px solid ${alpha(ganttColors.primary, 0.1)}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: ganttColors.primary }}>
          📅 SVAR Gantt for React
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Professional Gantt chart component for project and program management. 
          Features include drag-and-drop, progress tracking, dependencies, and multi-level task hierarchies.
          Based on the official <a href="https://svar.dev/react/gantt/#demo" target="_blank" rel="noopener noreferrer" style={{ color: ganttColors.primary, textDecoration: 'none', fontWeight: 600 }}>SVAR Gantt Demo</a>.
          Documentation: <a href="https://docs.svar.dev/react/gantt/overview" target="_blank" rel="noopener noreferrer" style={{ color: ganttColors.secondary, textDecoration: 'none', fontWeight: 600 }}>docs.svar.dev</a>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
          <Chip label="Interactive Timeline" size="small" color="primary" />
          <Chip label="Progress Bars" size="small" sx={{ bgcolor: ganttColors.success, color: 'white' }} />
          <Chip label="Tree Structure" size="small" sx={{ bgcolor: ganttColors.warning, color: 'white' }} />
          <Chip label="Customizable" size="small" sx={{ bgcolor: ganttColors.purple, color: 'white' }} />
          <Chip label="GPLv3 Licensed" size="small" sx={{ bgcolor: ganttColors.secondary, color: 'white' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default GanttShowcase;
